import { defineStore } from 'pinia'
import { ref, computed, nextTick, watch } from 'vue'
import { useCustomerStore } from "@/stores/CustomersStore"
import { useCalendarStore } from "@/views/apps/calendar/useCalendarStore"
import { useProductsStore } from "@/stores/ProductsStore"
import { useServiceStore } from "@/stores/ServicesStore"
import { useModalStore } from "@/stores/ModalStore"
import emitter from '@/utils/emitter'
import { generateUniqueId } from "@/utils/strings"
import { useI18n } from "vue-i18n"
import {
  extractDate,
  extractTime,
  getCurrentTime,
  extractTimeFromDateString,
  formatDateToLocalString,
  formatDateToLocalTimeString,
  getCurrentTimeRoundedToSlot,
  parseDateFromString,
  getMinutesDifference,
  timeToMinutes, minutesToTime, adjustDateTimeByMinutes,
  addMinutesToTime,
  extractDateFromString, isValidTime,
} from "@/utils/datetime"
import debounce from 'lodash/debounce'

let calendarApi = null

export const useEventStore = defineStore('eventsStore', () => {

  const calendarStore = useCalendarStore()
  const serviceStore = useServiceStore()
  const modalStore = useModalStore()
  const { t } = useI18n()
  const allowResetEvent = ref(true)


  // Set reference to the calendar API
  const setCalendarApi = (api) => {
    calendarApi = api
  }
  const getCalendarApi = () => {
    return calendarApi
  }

  // Statusy wizyt
  // zakończona: 1
  // potwierdzona: 2 <- standardowy status jaki dostaje wizyta po dodaniu do kalendarza
  // anulowana: 3
  // niepotwierdzona: 4
  // usunięta: 5
  // nie pokazuj: 6
  // potwierdzona przez klienta: 7
  // klient w salonie: 8

  const actions = ref({
    showCancel: false,
    showDelete: false,
    showEdit: false,
    showRestore: false,
    showSave: true,
  })

  const selectedLoyaltyCoupon = ref(null)
  const loyaltyPointsEarned = ref(0) // Points earned from the event, used in finalization view

  const blankEvent = {
    id: "",
    eventId: 0, // coresponds with FC event id eg. calendarApi.getEventById()
    type: 0,
    status: 2,
    title: "",
    dateStart: "", // 2021-09-01
    timeStart: "", // 09:00
    timeEnd: "", // 12:00
    client: "",
    employeeId: "",
    salonerClientId: "",
    description: "",
    reserveListEkey: "", // ekey of the reserve list - we know that this event came from the reserve list and we can update it
    servicesMode: "sequential", // sequential (false), parallel
    wantedNotif: false, // m: zmieniłem po konsultacji z Magdą i Łukaszem
    clientInSalon: false,
    clientConfirmed: false,
    clientComment: "",
    tags: [],
  }

  // States
  const selectedEvent = ref(JSON.parse(JSON.stringify(blankEvent)))
  const allowSave = ref(true)
  const isModified = ref(false)
  const isLoading = ref(false)
  const isSaving = ref(false) // B: nie chcem ale muszem
  const message = ref(null)
  const error = ref(null)
  const isCyclical = ref(false)
  const wasFinalisedAlready = ref(false) // Special case when we edit already finalised event
  const newFiles = ref([]) // Files selected for future upload
  //const existingFiles = ref([]) // Existing files after user modifications
  const areServicesScattered = ref(false) // If user mingles with combined visit services on calendar and eg. endTime of first service != endTime of next service then services are considered as scatered

  const blockadeOrNoteView = ref(false)
  const cyclicVisitView = ref(false)
  const visitView = ref(true)

  const showFinalization = ref(false)

  //  👉 Selected products in finalization view
  const selectedProducts = ref([])

  //  👉 Id of reserve item list - used to update the reserve list when new event is created directly from the reserve list
  const reserveListItemId = ref(null)

  //  👉 Selected services array
  const selectedServices = ref([])

  //  👉 Selected employee
  const selectedEmployee = computed({
    get: () => {
      // If we have employee selected for event eg. dayClick
      const foundEmployee = calendarStore.availableCalendars.find((obj) => obj.id === selectedEvent.value.employeeId)
      if (foundEmployee) return foundEmployee

      // How many employees are selected in filters
      const employeeCalendarIds = (calendarStore.calendarEmployeesCalendars || []).map(c => c.id)
      const selectedCalendarIds = (calendarStore.selectedCalendars || [])
      //const selectedEmployeeIds = selectedCalendarIds.filter(id => employeeCalendarIds.includes(id))
      const selectedEmployeeIds = employeeCalendarIds.filter(id => selectedCalendarIds.includes(id))

      // If more then one employee is selected - return it
      if (selectedEmployeeIds.length >= 1) {
        const singleId = selectedEmployeeIds[0]

        return calendarStore.availableCalendars.find((obj) => obj.id === singleId) || null
      } else { // More than one employee is selected - return first
        return calendarStore.calendarEmployeesCalendars[0]
      }

      // Lets hope it wont reach here
      return null
    },

    set: (value) => {
      if (value === null) selectedEvent.value.employeeId = null
      else {
        if (typeof value === "string") {
          selectedEvent.value.employeeId = value
        }
        if (typeof value === "object") {
          selectedEvent.value.employeeId = value.id
        }
      }
    },
  })

  //  👉 Services total price
  const tipAmount = ref(null)

  // 👉 Services total duration
  const servicesTotalDuration = computed(() => {
    if (selectedEvent.value.servicesMode === "parallel") { // "wykonywane w tym samym czasie"
      return selectedServices.value.reduce((maxDuration, service) => {
        const serviceDuration = Number(service.duration) || 0

        return serviceDuration > maxDuration ? serviceDuration : maxDuration
      }, 0)
    } else { // "wykonywane jedna po drugiej (normalnie)"
      return selectedServices.value.reduce((totalDuration, service) => {
        return totalDuration + (Number(service.duration) || 0)
      }, 0)
    }
  })

  // Sync selectedCustomer with customer store
  const customerStore = useCustomerStore()
  //  👉 Selected customer
  const selectedCustomer = computed({
    get: () => customerStore.selectedCustomer,
    set: (value) => {
      // noinspection JSConstantReassignment
      customerStore.selectedCustomer = value
    },
  })

  // Sync selectedSeller with products store
  const productsStore = useProductsStore()
  //  👉 Selected customer
  const selectedProductType = computed({
    get: () => productsStore.selectedProductType,
    set: (value) => {
      // noinspection JSConstantReassignment
      productsStore.selectedProductType = value
    },
  })
  //  👉 Selected employee who sells products
  const selectedSeller = computed({
    get: () => productsStore.selectedSeller,
    set: (value) => {
      // noinspection JSConstantReassignment
      productsStore.selectedSeller = value
    },
  })

  // Sync with selectedEvent
  //  👉 Selected date
  const selectedDate = computed({
    get: () => {
      // Check if selectedEvent.start is defined and not empty
      if (selectedEvent.value.dateStart) {
        return new Date(selectedEvent.value.dateStart)
      }

      // Return today's date if start is empty
      return new Date()
    },
    set: (value) => {
      let dateValue = value
      // Check if value is object
      if (typeof value === 'object') {
        dateValue = new Date(value)
      }
      // Check if value is string
      if (typeof value === 'string') {
        if (value.indexOf(".")) {
          let valueArr = value.split('.')
          value = valueArr[2] + "-" + valueArr[1] + "-" + valueArr[0]
        }
        value = value.replaceAll('undefined-', '') // Special cleaning for iOSres
        const parsedDate = new Date(value)
        if (!isNaN(parsedDate)) {
          dateValue = parsedDate
        } else {
          console.error("Invalid date string provided to selectedDate setter value:", value)
          console.error("Invalid date string provided to selectedDate setter parsedDate:", parsedDate)

          return
        }
      }

      // Ensure the value is a valid Date instance
      if (dateValue instanceof Date && !isNaN(dateValue)) {

        selectedEvent.value.dateStart = extractDate(dateValue, "-")

        // Update the temporary event on FC 🔥
        // nie dziala test datesSet
        // if (calendarApi) {
        //   //const tempEvent = calendarApi.getEventById('temp-event')
        //   const tempEvent = selectedEvent.value.eventId ? calendarApi.getEventById(selectedEvent.value.eventId) : calendarApi.getEventById('temp-event')
        //   if (tempEvent) {
        //     console.log('updejtuje tmp event 0', tempEvent)
        //     // tempEvent.setStart(selectedEvent.value.dateStart + " " + selectedEvent.value.timeStart) // Update the start time of the temporary event
        //     // tempEvent.setEnd(selectedEvent.value.dateStart + " " + selectedEvent.value.timeEnd) // Update the end time of the temporary event
        //     setTimeout(() => {
        //       tempEvent.value = selectedEvent.value.eventId ? calendarApi.getEventById(selectedEvent.value.eventId) : calendarApi.getEventById('temp-event')
        //       console.log('updejtuje tmp event 1', tempEvent)
        //       console.log('updejtuje tmp event date', selectedEvent.value.dateStart + " " + selectedEvent.value.timeStart)
        //       tempEvent.setStart(selectedEvent.value.dateStart + " " + selectedEvent.value.timeStart) // Update the start time of the temporary event
        //       tempEvent.setEnd(selectedEvent.value.dateStart + " " + selectedEvent.value.timeEnd) // Update the end time of the temporary event
        //     }, 1000)
        //   }
        // }
      } else {
        console.error("Invalid date string provided to selectedDate setter #2 type value:", typeof dateValue)
        console.error("Invalid date string provided to selectedDate setter #2 dateValue:", dateValue)
      }

    },

  })

  const ensureCorrectTimeString = (newTimeStr, oldTimeStr) => {
    //console.log('ensureCorrectTimeString', newTimeStr, oldTimeStr)
    if(/^([01]\d|2[0-3]):([0-5]\d)$/.exec(newTimeStr)) return newTimeStr //check if newTimeStr is in "HH:mm" format
    if(!(/^([01]\d|2[0-3]):([0-5]\d)$/.exec(oldTimeStr))) oldTimeStr = getCurrentTime(true)
    if(typeof newTimeStr === "string" && newTimeStr.indexOf(':') > 0 ) {
      let [newHH, newMm] = newTimeStr.split(':')
      let [oldHH, oldMm] = oldTimeStr.split(':')
      if(newHH === "HH" || newHH === "hh" || newHH === "" || newHH === " " || newHH === "NaN") newHH = oldHH
      if(newMm === "MM" || newMm === "mm" || newMm === "" || newMm === " " || newMm === "NaN") newMm = oldMm

      return newHH+":"+newMm
    } else return oldTimeStr
  }

  //  👉 Selected start time
  const selectedStartTime = computed({
    get: () => {
      return selectedEvent.value.timeStart
    },
    set: (value) => {

      if (!isValidTime(value)) {
        console.error('wrong start time', value)

        return
      }

      const oldTimeStart = selectedEvent.value.timeStart
      const oldTimeEnd = selectedEvent.value.timeEnd

      if (oldTimeStart === value) return

      let runServicesTimeAdjustments = true

      // Handle empty value
      if (value === '' && !isEmpty(oldTimeStart)) {
        value = oldTimeStart
        //runServicesTimeAdjustments = false
        selectedEvent.value.timeStart = value
        emitter.emit('time-changed', true)

        return
      }
      if (value === '' || value === null) value = getCurrentTimeRoundedToSlot(calendarStore.defaultSlotDuration)
      if (value.length > 5) {
        value = value.slice(0, 5)
      }
      value = ensureCorrectTimeString(value, oldTimeStart)

      // Handle same value
      //if (oldTimeStart === value) runServicesTimeAdjustments = false

      selectedEvent.value.timeStart = value
      // Shift timeEnd by the same delta as timeStart change, capped at 23:59 if crossing to next day
      const dateOnly = selectedEvent.value.dateStart ? selectedEvent.value.dateStart : extractDate(new Date(), '-')
      if (selectedEvent.value.timeEnd && oldTimeStart) {
        const oldStartDate = new Date(dateOnly + " " + oldTimeStart)
        const newStartDate = new Date(dateOnly + " " + value)
        if (!isNaN(oldStartDate) && !isNaN(newStartDate)) {
          const deltaMs = newStartDate.getTime() - oldStartDate.getTime()
          if (deltaMs !== 0) {
            const currentEndDate = new Date(dateOnly + " " + selectedEvent.value.timeEnd)
            let newEndDate = new Date(currentEndDate.getTime() + deltaMs)
            const endOfDay = new Date(dateOnly + " 23:59")
            if (newEndDate > endOfDay) newEndDate = endOfDay

            const newEndTime = extractTime(newEndDate)
            if (newEndTime !== selectedEvent.value.timeEnd) {
              selectedEvent.value.timeEnd = newEndTime
              emitter.emit('time-changed', true)
            }
          }
        }
      }


      if (isWatcherActive.value && runServicesTimeAdjustments) {
        adjustServicesTimes(oldTimeStart, oldTimeEnd)
      }
    },
  })

  //  👉 Selected end time
  const selectedEndTime = computed({
    get: () => {
      return selectedEvent.value.timeEnd
    },
    set: (value) => {

      if (!isValidTime(value)) {
        console.error('wrong end time', value)

        return
      }

      const oldTimeStart = selectedEvent.value.timeStart
      const oldTimeEnd = selectedEvent.value.timeEnd
      let runServicesTimeAdjustments = true

      if (oldTimeStart === value) return

      // Handle empty value
      if (value === '' && !isEmpty(oldTimeEnd)) {
        value = oldTimeEnd
        //runServicesTimeAdjustments = false
        selectedEvent.value.timeEnd = value
        emitter.emit('time-changed', true)

        return
      }
      if (value === '' || value === null) value = getCurrentTimeRoundedToSlot(calendarStore.defaultSlotDuration)
      if (value.length > 5) {
        value = value.slice(0, 5)
      }
      value = ensureCorrectTimeString(value, oldTimeStart)

      // Handle same value
      //if (oldTimeEnd === value) runServicesTimeAdjustments = false

      if ((new Date(selectedEvent.value.dateStart + " " + value)) <= (new Date(selectedEvent.value.dateStart + " " + selectedEvent.value.timeStart))) { // Prevent end time to be earlier than start time
        const startDateTime = new Date(selectedEvent.value.dateStart + " " + selectedEvent.value.timeStart)
        const correctTime = new Date(startDateTime.getTime() + 15 * 60 * 1000)
        selectedEvent.value.timeEnd = extractTime(correctTime)
        emitter.emit('time-changed', true)
      } else {
        selectedEvent.value.timeEnd = value
      }
      // B: sync off
      // Update temporary event on FC 🔥
      // if (calendarApi) {
      //   const tempEvent = selectedEvent.value.eventId ? calendarApi.getEventById(selectedEvent.value.eventId) : calendarApi.getEventById('temp-event')
      //   if (tempEvent) {
      //     tempEvent.setEnd(selectedEvent.value.dateStart + " " + selectedEvent.value.timeEnd)
      //   }
      // }

      //adjustResizedServicesTimes(selectedEvent.value.timeStart, value)

      //console.log('selectedEndTime2', isWatcherActive.value, runServicesTimeAdjustments)
      if (isWatcherActive.value && runServicesTimeAdjustments) {
        adjustServicesTimes(oldTimeStart, oldTimeEnd)
      }

    },
  })

  // Adjust services time when start and end time are changed in pickers (method invoked on blur)
  // with slight delay
  const adjustServicesTimes = debounce(async (oldStartTime = false, oldEndTime = false) => {
    console.warn('adjustServicesTimes')
    //setTimeout(() => {

    // If there is no services just end it here and only update stuff on FC
    if (selectedServices.value.length === 0) {
      updateSelectedEventOnFullcalendar()

      return
    }

    let newStartTime = selectedEvent.value.timeStart
    let newEndTime = selectedEvent.value.timeEnd

    const getServiceSafeStartDateTime = (service) => {
      return service.start && extractTimeFromDateString(service.start) === service.startTime ? new Date(service.start) : new Date(selectedEvent.value.dateStart + " " + service.startTime) // Depends if service newly added by user or existed (added from BE)
    }
    const getServiceSafeEndDateTime = (service) => {
      return service.end && extractTimeFromDateString(service.end) === service.endTime ? new Date(service.end) : new Date(selectedEvent.value.dateStart + " " + service.endTime) // Depends if service newly added by user or existed (added from BE)
    }

    const getTotalDuration = () => {
      return selectedServices.value.reduce((sum, service) => {
        const start = getServiceSafeStartDateTime(service)
        const end = getServiceSafeEndDateTime(service)

        return sum + (end - start) / 60000 // minutes
      }, 0)
    }

    const roundTo1 = (minutes) => Math.floor(minutes)
    const minDuration = 1

    const originalTotalDuration = getTotalDuration()
    const dateOnly = selectedEvent.value.dateStart
    const newStart = new Date(dateOnly + " " + newStartTime)
    const newEnd = new Date(dateOnly + " " + newEndTime)
    const newTotalDuration = (newEnd - newStart) / 60000

    const remainingDifference = Math.abs(originalTotalDuration - newTotalDuration)
    const isReduction = originalTotalDuration > newTotalDuration

    let usedSpecialScatteredLogic = false

    if (areServicesScattered.value && oldStartTime && oldEndTime) {
      const oldStart = new Date(dateOnly + " " + oldStartTime)
      const oldEnd = new Date(dateOnly + " " + oldEndTime)
      if (!isNaN(oldStart) && !isNaN(oldEnd)) {
        const oldTotalDuration = (oldEnd - oldStart) / 60000
        const shiftMs = newStart.getTime() - oldStart.getTime()

        // Case 1: visit duration unchanged -> just shift each service by the same delta
        if (Math.round(newTotalDuration) === Math.round(oldTotalDuration)) {
          const servicesSorted = [...selectedServices.value].sort((a, b) => getServiceSafeStartDateTime(a) - getServiceSafeStartDateTime(b))
          for (let i = 0; i < servicesSorted.length; i++) {
            const service = servicesSorted[i]
            const sStart = getServiceSafeStartDateTime(service)
            const sEnd = getServiceSafeEndDateTime(service)
            const serviceOldEndTimeStr = service.endTime ? service.endTime : extractTime(sEnd)
            const endedAtOldVisitEnd = serviceOldEndTimeStr === oldEndTime

            const shiftedServiceStartDate = new Date(sStart.getTime() + shiftMs)
            let shiftedServiceEndDate = new Date(sEnd.getTime() + shiftMs)

            service.start = formatDateToLocalString(shiftedServiceStartDate)
            service.startTime = extractTimeFromDateString(service.start)
            service.end = formatDateToLocalString(shiftedServiceEndDate)
            service.endTime = extractTimeFromDateString(service.end)
            service.duration = (shiftedServiceEndDate - shiftedServiceStartDate) / 60000

            // Ensure services that used to end exactly at visit end still end exactly at new visit end
            if (endedAtOldVisitEnd) {
              service.end = formatDateToLocalString(newEnd)
              service.endTime = formatDateToLocalTimeString(newEnd)
              service.duration = (newEnd - shiftedServiceStartDate) / 60000
            }
          }
          usedSpecialScatteredLogic = true
        } else {
          // Case 2: visit duration changed
          const servicesSorted = [...selectedServices.value].sort((a, b) => getServiceSafeStartDateTime(a) - getServiceSafeStartDateTime(b))
          if (isReduction) {
            // Shorter visit: proportionally reduce service durations and lay them out sequentially to avoid overlaps
            let currentStart = new Date(newStart)
            for (let i = 0; i < servicesSorted.length; i++) {
              const service = servicesSorted[i]
              const sStart = getServiceSafeStartDateTime(service)
              const sEnd = getServiceSafeEndDateTime(service)
              const duration = (sEnd - sStart) / 60000
              const serviceOldEndTimeStr = service.endTime ? service.endTime : extractTime(sEnd)
              const endedAtOldVisitEnd = serviceOldEndTimeStr === oldEndTime
              let newDuration = duration - (duration / originalTotalDuration) * remainingDifference
              if (duration >= minDuration) {
                newDuration = Math.max(minDuration, roundTo1(newDuration))
              } else {
                newDuration = duration
              }
              let serviceEndDate = new Date(currentStart.getTime() + newDuration * 60000)
              service.start = formatDateToLocalString(currentStart)
              service.startTime = extractTimeFromDateString(service.start)
              service.end = formatDateToLocalString(serviceEndDate)
              service.endTime = extractTimeFromDateString(service.end)
              service.duration = (serviceEndDate - currentStart) / 60000
              const finalDuration = (serviceEndDate - currentStart) / 60000
              //console.log(`NST (scattered reduction): ${service.name}, Start: ${service.start}, End: ${service.end}, Duration: ${finalDuration} min`)

              // Keep alignment to visit end if previously aligned
              if (endedAtOldVisitEnd) {
                service.end = formatDateToLocalString(newEnd)
                service.endTime = formatDateToLocalTimeString(newEnd)
                service.duration = (newEnd - currentStart) / 60000
                if (i === servicesSorted.length - 1) serviceEndDate = newEnd
              }

              currentStart = serviceEndDate
            }
          } else {
            // Longer visit: keep individual durations, place sequentially starting at newStart to avoid overlaps
            let currentStart = new Date(newStart)
            for (let i = 0; i < servicesSorted.length; i++) {
              const service = servicesSorted[i]
              const sStart = getServiceSafeStartDateTime(service)
              const sEnd = getServiceSafeEndDateTime(service)
              const duration = (sEnd - sStart) / 60000
              const serviceOldEndTimeStr = service.endTime ? service.endTime : extractTime(sEnd)
              const endedAtOldVisitEnd = serviceOldEndTimeStr === oldEndTime
              let serviceEndDate = new Date(currentStart.getTime() + duration * 60000)
              service.start = formatDateToLocalString(currentStart)
              service.startTime = extractTimeFromDateString(service.start)
              service.end = formatDateToLocalString(serviceEndDate)
              service.endTime = extractTimeFromDateString(service.end)
              service.duration = (serviceEndDate - currentStart) / 60000
              const finalDuration = (serviceEndDate - currentStart) / 60000
              //console.log(`NST (scattered extension): ${service.name}, Start: ${service.start}, End: ${service.end}, Duration: ${finalDuration} min`)

              // Keep alignment to visit end if previously aligned
              if (endedAtOldVisitEnd) {
                service.end = formatDateToLocalString(newEnd)
                service.endTime = formatDateToLocalTimeString(newEnd)
                service.duration = (newEnd - currentStart) / 60000
                if (i === servicesSorted.length - 1) serviceEndDate = newEnd
              }

              currentStart = serviceEndDate
            }
          }
          usedSpecialScatteredLogic = true
        }
      }
      //emitter.emit('update-all-services-time-pickers')
    }

    // Default behavior for non-scattered or when old times are missing: proportional allocation
    if (!usedSpecialScatteredLogic) {
      let currentStart = new Date(newStart)
      for (let i = 0; i < selectedServices.value.length; i++) {
        const service = selectedServices.value[i]
        const start = getServiceSafeStartDateTime(service)
        const end = getServiceSafeEndDateTime(service)
        const duration = (end - start) / 60000

        let newDuration = isReduction
          ? duration - (duration / originalTotalDuration) * remainingDifference
          : duration + (duration / originalTotalDuration) * remainingDifference

        if (isReduction) {
          if (duration >= minDuration) {
            newDuration = Math.max(minDuration, roundTo1(newDuration))
          } else {
            newDuration = duration
          }
        } else {
          if (duration < minDuration) {
            newDuration = duration
          } else {
            newDuration = roundTo1(newDuration)
          }
        }

        const serviceEndDate = new Date(currentStart.getTime() + newDuration * 60000)

        service.start = formatDateToLocalString(currentStart)
        service.startTime = extractTimeFromDateString(service.start)
        service.end = formatDateToLocalString(serviceEndDate)
        service.endTime = extractTimeFromDateString(service.end)
        service.duration = (serviceEndDate - currentStart) / 60000

        const finalDuration = (serviceEndDate - currentStart) / 60000
        console.log(`NST (default): ${service.name}, Start: ${service.start}, End: ${service.end}, Duration: ${finalDuration} min`)

        currentStart = serviceEndDate
      }

      // Make sure last service ends at the same time as newEnd
      if (selectedServices.value.length > 0) {
        selectedServices.value[selectedServices.value.length - 1].end = formatDateToLocalString(newEnd)
        selectedServices.value[selectedServices.value.length - 1].endTime = formatDateToLocalTimeString(newEnd)
        selectedServices.value[selectedServices.value.length - 1].duration = roundTo1((newEnd - (new Date(selectedServices.value[selectedServices.value.length - 1].start))) / 60000)
      }
    }

    updateServiceResourcesStatus()
    detectConflictsInServicesAndEmployeeWorkingHours()
  }, 200)

  // Flag to prevent auto endtime recalculations
  const allowRecalculation = ref(true)
  const setAllowEndTimeRecalculation = (value) => {
    allowRecalculation.value = value
  }

  const recalculateStartTime = (force = false) => {
    if (!allowRecalculation.value && !force) {
      console.warn('Start time recalculation is disabled')

      return
    }
    if (!selectedEvent.value.dateStart || !selectedEvent.value.timeStart || selectedServices.value.length === 0) return

    // If we have services with duration
    if (servicesTotalDuration.value > 0) {
      const firstService = selectedServices.value.reduce((min, s) =>
        !min || s.endTime < min.startTime ? s : min
      , null)

      if (firstService) selectedEvent.value.timeStart = firstService.startTime

      emitter.emit('time-changed', true)
    }
  }

  const recalculateEndTime = (force = false) => {
    if (!allowRecalculation.value && !force) {
      //console.warn('End time recalculation is disabled')

      return
    }
    //console.log('recalculateEndTime')
    if (selectedEvent.value.dateStart && selectedEvent.value.timeStart && selectedServices.value.length === 0) {
      //adjustDateTimeByMinutes()
      selectedEvent.value.timeEnd = addMinutesToTime(selectedEvent.value.timeStart, 15)
      emitter.emit('time-changed', true)

      return
    }
    if (!selectedEvent.value.dateStart || !selectedEvent.value.timeStart || selectedServices.value.length === 0) return

    // If we have services with duration
    if (servicesTotalDuration.value > 0) {
      // const startDateTime = new Date(selectedEvent.value.dateStart + " " + selectedEvent.value.timeStart)
      // const endDateTime = new Date(startDateTime.getTime() + servicesTotalDuration.value * 60 * 1000)
      // selectedEvent.value.timeEnd = extractTime(endDateTime)
      //// const lastService = selectedServices.value.at(-1)
      const lastService = selectedServices.value.reduce((max, s) =>
        !max || s.endTime > max.endTime ? s : max
      , null)
      if (lastService) selectedEvent.value.timeEnd = lastService.endTime
      // TODO B
      //console.log('%cendTime', 'color:green', lastService.endTime)
      emitter.emit('time-changed', true)
    }
  }

  // m: test
  const getResourceById = (resourceId) => {
    return calendarStore.resourcesCalendars.find(
      (resource) => resource.id === resourceId,
    )?.title || null
  }

  // m: test
  // Get resources for a specific service
  const getServiceResources = (serviceIndex) => {
    return selectedEvent.value.services?.[serviceIndex]?.resources || []
  }

  //  👉 Add product to event
  const addProductToEvent = (product) => {
    let employeeThatSold = calendarStore.employeesCalendars.find(cal => cal.id === product.employeeEkey)
    if (!employeeThatSold && calendarStore.employeesCalendars.length > 0) {
      employeeThatSold = calendarStore.employeesCalendars[0]
    }
    const totalValue = Number(product.price) * Number(product.quantity)
    const finalPrice = totalValue - Number(product.discount)
    let newProduct = {
      id: product.id,
      title: product.title,
      unit: product.unit,
      price: product.price,
      quantity: product.quantity,
      capacity: product.capacity,
      discountType: product.discountType,
      discountValue: product.discountValue,
      discount: product.discount,
      type: product.type,
      finalPrice: finalPrice,
      seller: employeeThatSold,
      voucherDetails: product.voucherDetails || null,
      isVoucher: product.isVoucher || null,
      dynamicId: generateUniqueId(),
    }
    if (product.voucherEntityId && product.voucherDetails) {
      newProduct = {
        ...newProduct,
        voucher: 1,
        identifier: product.voucherDetails.identifier,
        voucherType: Number(product.voucherDetails.type),
        voucherValue: Number(product.voucherDetails.value),
        isValueEditable: false,
      }
      if (Number(product.voucherDetails.type) === 2) { // KARNET
        newProduct = {
          ...newProduct,
          services: product.voucherDetails.services,
        }
      }
    }
    if (product.isVoucher) { // KARNET lub BON
      newProduct = {
        ...newProduct,
        voucherExpirationDate: new Date(product.voucherDetails.timeout),
      }
      // B: there is no need to update .voucherDetails.timeout here cause its in "2025-01-29" format already
    }
    selectedProducts.value.push(newProduct)
  }

  //  👉 Add service to event
  const addServiceToEvent = async (service) => {
    if (!service) return

    let tempSelectedEmployee = calendarStore.employeesCalendars.find(e => e.id === service.employeeEkey)
    // Employee not found? Maybe he was deleted?
    if (!tempSelectedEmployee) tempSelectedEmployee = calendarStore.unavailableEmployees.find(e => e.id === service.employeeEkey)
    // Still not found? Not possible but ... we go with selectedEmployee
    if (!tempSelectedEmployee) tempSelectedEmployee = selectedEmployee.value

    let priceMin = null
    let priceMax = null
    const originalService = serviceStore.servicesArray.find(s => s.employeeEkey === service.employeeEkey && s.ekey === service.ekey)
    if(originalService) {
      priceMin = Number(originalService.price)
      priceMax = Number(originalService.priceMax)
    }
    //console.log('originalService', originalService)

    const newService = {
      ...service,
      selectedEmployee: tempSelectedEmployee,
      dynamicId: generateUniqueId(),
      discountType: service.discountType,
      discountValue: service.discountValue,
      discount: service.discount,
      price: Number(service.price),
      priceMin: priceMin,
      priceMax: priceMax,
      // discountCash: service.discountType === "solid" ? service.discountValue : 0,
      // discountPercent: service.discountType === "percent" ? service.discountValue : 0,
    }

    // newService.finalPrice = calculateFinalPrice(newService)
    newService.finalPrice = serviceStore.calculateServicePrice(newService.price, newService.discountType, newService.discountValue)

    //Check if service has startTime and endTime if not add and calculate them
    if (!newService.startTime || !newService.endTime) {
      if (!newService.start || !newService.end) { // Service added from "Wybierz usługę dla:..." in modal
        const duration = Number(newService.duration || 0) * 60 * 1000 // Need to convert duration to milliseconds
        if (selectedServices.value.length === 0 || selectedEvent.value.servicesMode === "parallel") { // This is the first service or mode is parallel
          const startDateTime = new Date(extractDate(selectedDate.value, '-') + " " + selectedStartTime.value)
          const endDateTime = new Date(startDateTime.getTime() + duration)
          newService.startTime = extractTime(startDateTime)
          newService.endTime = extractTime(endDateTime)
        } else { // This is another service or mode is sequential
          const lastService = selectedServices.value[selectedServices.value.length - 1]
          const lastServiceEndTime = new Date(extractDate(selectedDate.value, '-') + " " + lastService.endTime)
          const endDateTime = new Date(lastServiceEndTime.getTime() + duration)
          newService.startTime = extractTime(lastServiceEndTime)
          newService.endTime = extractTime(endDateTime)
        }
        // If it's 23:30 and u add service from reserve list that last 60 min this code below prevents shit from hitting the fan
        if (newService.endTime < newService.startTime) {
          newService.endTime = "23:59"
          newService.duration = getMinutesDifference(newService.startTime, newService.endTime)
        }

        newService.start = extractDate(selectedDate.value, '-') + ' ' + newService.startTime
        newService.end = extractDate(selectedDate.value, '-') + ' ' + newService.endTime

      } else { // Service added from BE eg. in handleEventClick
        newService.startTime = extractTimeFromDateString(newService.start)
        newService.endTime = extractTimeFromDateString(newService.end)
      }
    }

    // Add the new service //
    selectedServices.value = [...selectedServices.value, newService]

    recalculateEndTime()

    // Update resources status (change color if slot is taken)
    await nextTick()
    updateServiceResourcesStatus(service)

  }

  //  👉 Check employees working hours vs selected services
  const detectConflictsInServicesAndEmployeeWorkingHours = () => {
    if (calendarApi.view.type === "dayGridMonth") return // no resources for month view so we cant calculate nothing...
    //console.log('detectConflictsInServicesAndEmployeeWorkingHours')
    const allMessages = new Set()
    selectedServices.value.forEach(service => {
      service.employeeWorkHoursStatus = true
      service.employeeWorkHoursMessage = []
      const employeeCalendar = calendarStore.calendarEmployeesCalendars.find(
        emp => emp.id === service.employeeEkey,
      )
      if (!employeeCalendar) return

      let workStart = ''
      let workEnd = ''

      if (calendarApi.view.type === "resourceTimeGridDay") {
        if (employeeCalendar.workHours === null || employeeCalendar.workHours.length === 0 || employeeCalendar.workHours.indexOf(' - ') <= 0) {
          service.employeeWorkHoursStatus = false
          const message = `${employeeCalendar.title} ${t('doesNotWorkOnThisDay')}`
          if (!allMessages.has(message)) {
            allMessages.add(message)
            service.employeeWorkHoursMessage.push(message)
          }
          //service.employeeWorkHoursMessage.push(`${employeeCalendar.title} ${t('doesNotWorkOnThisDay')}`)
          //return
        }
        try {
          workStart = employeeCalendar.workHours.split(' - ')[0]
          workEnd = employeeCalendar.workHours.split(' - ')[1]
        } catch (e) {
          //
        }
      }

      if (calendarApi.view.type === "timeGridWeek" && employeeCalendar.weekWorkHours !== null) {
        const weekDayWorkingHours = employeeCalendar.weekWorkHours[extractDate(selectedDate.value, '-')]
        if (weekDayWorkingHours && (weekDayWorkingHours.length === 0 || weekDayWorkingHours.indexOf(' - ') <= 0)) {
          service.employeeWorkHoursStatus = false
          const message = `${employeeCalendar.title} ${t('doesNotWorkOnThisDay')}`
          if (!allMessages.has(message)) {
            allMessages.add(message)
            service.employeeWorkHoursMessage.push(message)
          }
          //service.employeeWorkHoursMessage.push(`${employeeCalendar.title} ${t('doesNotWorkOnThisDay')}`)
          //return
        }
        try {
          const [weekWorkStart, weekWorkEnd] = weekDayWorkingHours.split(' - ')
          workStart = weekWorkStart
          workEnd = weekWorkEnd
        } catch (e) {
          //console.log('err', e)
        }
      }

      // CHECK WORK HOURS
      if ((workStart?.length && workEnd?.length) && (service.startTime < workStart || service.endTime > workEnd)) {
        service.employeeWorkHoursStatus = false
        const message = `${employeeCalendar.title} ${t('worksThisDayBetween')} ${workStart} - ${workEnd}`
        if (!allMessages.has(message)) {
          allMessages.add(message)
          service.employeeWorkHoursMessage.push(message)
          service.employeeWorkHoursStatus = false
        }
        //service.employeeWorkHoursMessage.push(`${employeeCalendar.title} ${t('worksThisDayBetween')} ${workStart} - ${workEnd}`)
        //return
      }

      // CHECK EXISTING BLOCKADES
      const blockadeEvents = calendarStore.calendarApi.getEvents().filter(e =>
        Number(e.id) !== Number(selectedEvent.value.id) &&
        !e.extendedProps.isResource &&
        e.extendedProps.rendering !== 'background' &&
        (Number(e.extendedProps.eventType) === 2 || Number(e.extendedProps.eventType) === 0) && // Blockade || Normal visit
        e.extendedProps.employeeEkey === service.employeeEkey,
      ).sort((a, b) =>
        new Date(a.extendedProps.full_start) - new Date(b.extendedProps.full_start),
      )
      blockadeEvents.forEach(e => {
        const blockadeStartTime = extractTimeFromDateString(e.extendedProps.full_start)
        const blockadeEndTime = extractTimeFromDateString(e.extendedProps.full_end)
        const hasConflict = service.startTime < blockadeEndTime
          && service.endTime > blockadeStartTime
          && extractDateFromString(e.extendedProps.full_start) ===  extractDate(selectedDate.value, '-')

        // if ((service.startTime < blockadeEndTime && service.startTime > blockadeStartTime) || (service.endTime < blockadeEndTime && service.endTime > blockadeStartTime) ||
        //     (blockadeStartTime > service.startTime && blockadeEndTime < service.endTime) || (blockadeStartTime === service.startTime && blockadeEndTime === service.endTime) ||
        //     (blockadeStartTime === service.startTime && service.endTime > blockadeEndTime) || (blockadeStartTime < service.startTime && service.endTime === blockadeEndTime)
        // )
        if (hasConflict && calendarStore.viewSettings.showCalendarConflicts) {
          service.employeeWorkHoursStatus = false
          //service.employeeWorkHoursMessage.push(`${employeeCalendar.title} ${Number(e.extendedProps.eventType) === 2 ? t('hasBlockadeBetween') : t('hasOtherEventBetween')} ${blockadeStartTime} - ${blockadeEndTime}`)
          const message = `${employeeCalendar.title} ${
            Number(e.extendedProps.eventType) === 2
              ? t('hasBlockadeBetween')
              : t('hasOtherEventBetween')
          } ${blockadeStartTime} - ${blockadeEndTime}`

          if (!allMessages.has(message)) {
            allMessages.add(message)
            service.employeeWorkHoursMessage.push(message)
          }
        }
      })





    })
  }

  // 👉 Checks by how many minutes the total duration of all services exceeded the initial day
  const getServicesOverflowMinutes = () => {
    if (selectedServices.value.length === 0) return 0

    const eventDate = extractDate(selectedDate.value, '-')
    const startTime = selectedStartTime.value
    const startDateTime = new Date(`${eventDate} ${startTime}`)
    const endOfDay = new Date(startDateTime)
    endOfDay.setHours(23, 59, 59, 999)

    let maxEndDateTime

    if (selectedEvent.value.servicesMode === "parallel") {
      maxEndDateTime = startDateTime
      selectedServices.value.forEach(service => {
        let serviceEndDateTime

        if (service.endTime) {
          serviceEndDateTime = new Date(`${eventDate} ${service.endTime}`)
          // If end is earlier then start, means it over midnight
          if (serviceEndDateTime <= startDateTime && service.duration) {
            serviceEndDateTime.setDate(serviceEndDateTime.getDate() + 1)
          }
        } else if (service.end) {
          serviceEndDateTime = new Date(service.end)
        } else if (service.duration) {
          serviceEndDateTime = new Date(startDateTime.getTime() + Number(service.duration) * 60 * 1000)
        } else {
          return // If there is no endtime we dont give a duck
        }

        if (serviceEndDateTime > maxEndDateTime) {
          maxEndDateTime = serviceEndDateTime
        }
      })
    } else {
      let currentTime = startDateTime

      selectedServices.value.forEach(service => {
        const serviceDuration = Number(service.duration) || 0
        currentTime = new Date(currentTime.getTime() + serviceDuration * 60 * 1000)
      })

      maxEndDateTime = currentTime
    }

    if (maxEndDateTime > endOfDay) {
      const overflowMilliseconds = maxEndDateTime - endOfDay

      return Math.ceil(overflowMilliseconds / 60000)
    }

    return 0
  }

  //  👉 Check if services are misalignmented / scattered (kurwa rozpieprzone po polskiemu)
  const hasAdjacencyMisalignment = () => {
    //if (selectedEvent.value.servicesMode === "parallel" || selectedServices.value.length < 2) return false
    if (selectedServices.value.length < 2) return false
    for (let i = 0; i < selectedServices.value.length - 1; i++) {
      const current = selectedServices.value[i]
      const next = selectedServices.value[i + 1]
      const currentEnd = current?.endTime || (current?.end ? extractTime(new Date(current.end)) : null)
      const currentStart = current?.startTime || (current?.start ? extractTime(new Date(current.start)) : null)
      const nextStart = next?.startTime || (next?.start ? extractTime(new Date(next.start)) : null)
      if (currentEnd && nextStart && currentEnd !== nextStart) {
        //console.log('%c*hasAdjacencyMisalignment', 'color: #3cc412', true)

        return true
      }
    }
    const allSameEmployee = selectedServices.value.every(
      s => s.employeeEkey === selectedServices.value[0]?.employeeEkey,
    )
    if (!allSameEmployee) return true


    //console.log('%c*hasAdjacencyMisalignment', 'color: red', false)

    return false
  }

  //  👉 Recalculate start and end time of all services
  const recalculateAllServicesTime = (changedService = null, allowEndTimeRecalculation = true, forceUpdateServicesTimePickers = false) => {
    if (selectedServices.value.length === 0) {
      allowRecalculation.value = allowEndTimeRecalculation
      recalculateEndTime()

      return
    }

    if (hasAdjacencyMisalignment() && changedService) {
      const currentStartTime = new Date(extractDate(selectedDate.value, '-') + " " + changedService.startTime)
      const duration = Number(changedService.duration || 0) * 60 * 1000 // Convert duration to milliseconds
      const service = selectedServices.value.find(s => s.dynamicId === changedService.dynamicId)
      if (service) {
        const endDateTime = new Date(currentStartTime.getTime() + duration)
        const endOfDay = new Date(currentStartTime)
        endOfDay.setHours(23, 59, 0, 0)
        service.endTime = extractTime(endDateTime > endOfDay ? endOfDay : endDateTime)

        //emitter.emit('service-time-changed', service)
        //emitter.emit('update-all-services-time-pickers')
      }
    } else { // Normal case
      let currentStartTime = new Date(extractDate(selectedDate.value, '-') + " " + selectedStartTime.value)
      selectedServices.value = selectedServices.value.map((service, index) => {
        let duration = Number(service.duration || 0) * 60 * 1000 // Convert duration to milliseconds
        if (duration >= 86400000) duration = 86399999 // Dont allow more then 1 day of duration
        const endDateTime = new Date(currentStartTime.getTime() + duration)

        const updatedService = {
          ...service,
          startTime: extractTime(currentStartTime),
          endTime: extractTime(endDateTime),
        }

        if (selectedEvent.value.servicesMode !== "parallel") {
          currentStartTime = new Date(endDateTime)
        }

        return updatedService
      })
    }

    //emitter.emit('update-all-services-time-pickers')

    allowRecalculation.value = allowEndTimeRecalculation
    recalculateEndTime()
  }

  //  👉 Delete service from event
  const deleteServiceFromEvent = (service) => {
    if (!service) return

    const index = selectedServices.value.findIndex(s => s.dynamicId === service.dynamicId)
    if (index !== -1) {
      selectedServices.value = selectedServices.value.filter((_, i) => i !== index)
    }
    //recalculateEndTime()
    recalculateAllServicesTime(null, true)
  }

  // 👉 Update changes from edit time-price modal
  const updateServiceChanges = async (currentService) => {
    try {
      if (!currentService) {
        console.warn('No service selected')
      }

      // Find proper service
      const serviceIndex = selectedServices.value.findIndex(
        service => service.dynamicId === currentService.dynamicId,
      )

      if (serviceIndex === -1) {
        console.warn('Service not found')
      }

      // Update the actual discount amounts
      let discountAmount = 0
      if (currentService.discountType === 'solid' && currentService.discountValue) {
        discountAmount = Number(currentService.discountValue)
      } else if (currentService.discountType === 'percent' && currentService.discountValue) {
        discountAmount = (currentService.price * Number(currentService.discountValue)) / 100
      }

      // Calculate the final price
      const updatedPrice = serviceStore.calculateServicePrice(
        currentService.price,
        currentService.discountType,
        currentService.discountValue,
      )

      // Update service with all necessary values
      selectedServices.value[serviceIndex] = {
        ...currentService,
        discount: discountAmount,
        finalPrice: updatedPrice,
      }

      const overflowMinutes = getServicesOverflowMinutes()
      if (overflowMinutes > 0) {
        //console.warn('overflowMinutes', overflowMinutes)
        selectedServices.value[serviceIndex].duration -= overflowMinutes
        selectedServices.value = [...selectedServices.value]
        // Recalculate service times
        recalculateAllServicesTime(currentService, false)
        await nextTick()
        allowRecalculation.value = true
        recalculateEndTime()
      } else {
        // Recalculate service times
        recalculateAllServicesTime(currentService)
      }

      // Update resources status
      await nextTick()
      updateServiceResourcesStatus()

      return true
    } catch (error) {
      console.error('Error updating service:', error)
    }
  }

  // 👉 Updates services resources statuses
  //    Adds status key to resource
  //    true => resource does not collide with other resources in service timeframe
  //    false => collision detected
  //
  //    newlyAddedService -> #869d8qhbc needed to choose resource in "OR" service mode
  //
  const updateServiceResourcesStatus = (newlyAddedService = null) => {
    //console.log('%cNew service times: updateServiceResourcesStatus', 'color: #e84a07; font-weight: bold;')
    const day = extractDate(selectedDate.value, '-')
    if (!day) return

    // All resource events except the currently edited one
    const resourceEvents = calendarStore.fetchedEvents.filter(e =>
      e?.isResource === true && String(e.id) !== String(selectedEvent.value?.id),
    )

    selectedServices.value.forEach(service => {
      if (!service || !Array.isArray(service.resources) || service.resources.length === 0) return
      if (!service.startTime || !service.endTime) return

      // Service planned time window on that day
      const resourceStart = new Date(`${day} ${service.startTime}`)
      const resourceEnd = new Date(`${day} ${service.endTime}`)
      if (isNaN(resourceStart) || isNaN(resourceEnd)) return

      service.resources = service.resources?.map(resource => {
        // Consider only events for this resource
        const hasCollision = resourceEvents
          .filter(ev => String(ev.resourceId) === String(resource.id))
          .some(ev => {
            const eventStart = new Date(ev.start)
            const eventEnd = new Date(ev.end)
            if (isNaN(eventStart) || isNaN(eventEnd)) return false

            return resourceStart < eventEnd && resourceEnd > eventStart // Overlap if start < otherEnd AND end > otherStart
          })

        return {
          ...resource,
          status: !hasCollision, // true = free, false = collision
        }
      })
    })

    const lastService = Array.isArray(selectedServices.value)
      ? selectedServices.value[selectedServices.value.length - 1]
      : undefined
    if(newlyAddedService && newlyAddedService?.resourceMode === "OR" && lastService && lastService?.ekey === newlyAddedService?.ekey) {
      //console.log('asd', lastService)
      //console.log('cos tam removeBusyResources', lastService.resources.some(r => r.status))
      const chosenResource = lastService.resources.find(r => r.status)
      if(chosenResource) lastService.resources = [{ ...chosenResource }]
      //console.log('ostatni', lastService.resources)
    }
  }

  // 👉 Currently edited service
  const currentService = ref()

  const setCurrentService = (service) => {

    currentService.value = { ...service }

  }

  const clearCurrentService = () => {
    currentService.value = null
  }

  //---------- 👉 Payments: ----------//

  // 👉 Prepayment methods
  //   MONEY: 1,
  //   CARD: 2,
  //   BON: 3,
  //   BARTER: 4,
  //   TRANSFER: 5,
  //   PAYU: 6,
  //   BON_SALONER: 7,
  //   TPAY: 8,
  //   PASS_SALONER: 9,
  //   BLIK: 10,

  const isPrepaymentSectionDisabled = ref(false)
  const validationErrors = ref({
    prepayment: '',
    // etc.
  })
  const clearValidationErrors = () => {
    Object.keys(validationErrors.value).forEach(key => {
      validationErrors.value[key] = ''
    })
  }
  const emptyPrepaymentMethod = {
    id: 0,
    name: 'none',
    title: t('none'),
    voucher: null,
    sendSms: true,
    sendEmail: true,
    phone: '',
    email: '',
    url: '',
    status: '',
    amount: 0,
    enabled: true,
    checkout: false,
    prepayment: true,
    changedByHuman: false,
  }
  const selectedPrepaymentMethod = ref({ ...emptyPrepaymentMethod }) // Currently selected prepayment method

  const updateSelectedPrepaymentAmount = () => {
    // console.log('calendarStore.prepaymentSettings?.enabled', calendarStore.prepaymentSettings?.enabled)
    // console.log('allowSave.value', allowSave.value)
    // console.log('isPrepaymentSectionDisabled.value', isPrepaymentSectionDisabled.value)
    // console.log('selectedPrepaymentMethod.value.id', selectedPrepaymentMethod.value.id)
    // console.log('selectedPrepaymentMethod.value.changedByHuman', selectedPrepaymentMethod.value.changedByHuman)

    if (!calendarStore.prepaymentSettings?.enabled) return 0
    if (!allowSave.value || isPrepaymentSectionDisabled.value || selectedPrepaymentMethod.value.id === 0 || selectedPrepaymentMethod.value.changedByHuman) return 0

    const roundInt = n => Math.round(Number(n) || 0)

    const getServiceFinalPrice = service => {
      if (!service) return 0
      const price = service.finalPrice ?? serviceStore.calculateServicePrice(
        service.price,
        service.discountType,
        service.discountValue,
      )

      return Number(price) || 0
    }

    const getServicePrepayment = service => {
      if (!service) return 0

      const price = getServiceFinalPrice(service)

      let amount = Number(service.prepaymentAmount) || 0
      let type = Number(service.prepaymentType) || 0 // 1 = solid, 2 = percent

      if (amount <= 0) { // Fallback to store defaults
        amount = Number(calendarStore.prepaymentSettings?.defaultAmount) || 0
        type = Number(calendarStore.prepaymentSettings?.defaultType) || 0
      }

      if (amount <= 0) return 0

      if (type === 1) return roundInt(amount) // solid
      if (type === 2) {  // percent
        const pct = Math.min(Math.max(amount, 0), 100)

        return roundInt(price * pct / 100)
      }

      return 0
    }

    const total = selectedServices.value?.reduce((sum, service) => {
      return sum + getServicePrepayment(service)
    }, 0) ?? 0

    selectedPrepaymentMethod.value.amount = roundInt(total)

    return roundInt(total)
  }

  // 👉 Helper computed's for finalPrice
  const getServicesTotal = computed(() => {
    return selectedServices.value?.reduce((total, service) => {
      if (!service) return total

      return total + Number(service.finalPrice ?? serviceStore.calculateServicePrice(service.price, service.discountType, service.discountValue))
    }, 0) ?? 0
  })

  // Products total price
  const getProductsTotal = computed(() => {
    return selectedProducts.value?.reduce((total, product) => {

      return total + Number(product.finalPrice ?? 0)
    }, 0) ?? 0
  })

  // Only raw products total without vouchers
  const getProductsTotalNoVouchers = computed(() => {
    return selectedProducts.value?.reduce((total, product) => {
      if (product.isVoucher) return total

      return total + Number(product.finalPrice ?? 0)
    }, 0) ?? 0
  })

  // Vouchers total
  const getVouchersTotal = computed(() => {
    return selectedProducts.value?.reduce((total, product) => {
      if (product.isVoucher) {
        return total + Number(product.finalPrice ?? 0)
      }

      return total
    }, 0) ?? 0
  })

  // Apply coupon discount
  const applyCouponDiscount = (total) => {
    if (selectedLoyaltyCoupon.value) {
      const coupon = selectedLoyaltyCoupon.value
      if (coupon.discountType === 'PERCENT') {
        total -= total * (Number(coupon.value) / 100)
      } else {
        total -= Number(coupon.value)
      }
      if (total < 0) total = 0
    }

    return total
  }

  // Final price of the event without global discount
  const finalPrice = computed(() => {
    calculateEarnedLoyaltyPoints(getProductsTotalNoVouchers.value, getVouchersTotal.value)

    let total = getServicesTotal.value + getProductsTotal.value + Number(tipAmount.value) - Number(passesTotalReduction.value)
    finalPriceWithNoDiscount.value = Number(total.toFixed(2))

    total = applyCouponDiscount(total)

    return Number(total.toFixed(2))
  })

  // Total event price after discount from loyalty coupon
  const finalPriceWithNoDiscount = ref(0)

  // 👉 Payment methods amounts in finalization view and payment
  // default selected payment option
  // Tracks which payment methods are currently selected
  // Default
  const selectedPaymentMethods = ref([])

  //  👉 This is not real change it is a difference :)
  const balanceAmount = computed(() => {
    return Math.round(Number(preAndPaymentMethodsTotal.value) * 100) / 100 - Math.round(Number(finalPrice.value) * 100) / 100
  })

  //  👉 The total amount of all discounts
  const totalDiscountAmount = computed(() => {
    // Calculate service discounts
    const serviceDiscounts = selectedServices.value?.reduce((total, service) => {
      if (!service) return total

      let originalPrice = Number(service.price ?? 0)
      let discountAmount = 0

      if (service.discountType === 'percent' && service.discountValue) {
        // Percentage discount
        discountAmount = originalPrice * (Number(service.discountValue) / 100)
      } else if (service.discountType === 'solid' && service.discountValue) {
        // Cash/solid discount
        discountAmount = Number(service.discountValue)
      }

      return total + discountAmount
    }, 0) ?? 0

    // Calculate product discounts
    const productDiscounts = selectedProducts.value?.reduce((total, product) => {
      if (!product) return total

      // Use the pre-calculated discount value directly
      return total + Number(product.discount || 0)
    }, 0) ?? 0

    return serviceDiscounts + productDiscounts
  })

  // 👉 Sum reductions from saloner passes (karnety)
  const passesTotalReduction = computed(() => {
    // Get passes only
    const passPaymentMethods = selectedPaymentMethods.value?.filter(pm => pm.name === "pass_saloner") ?? []

    // Create array of all available services from passes with full voucher info
    const availablePassServices = []
    passPaymentMethods.forEach(pm => {
      pm.used = false // !!! Reset used state for payment method
      const services = pm.voucher?.services ?? []
      services.forEach(service => {
        // Create individual entries for each usesLeft
        for (let i = 0; i < (Number(service.usesLeft) || 0); i++) {
          availablePassServices.push({
            serviceId: Number(service.serviceId),
            voucherEntityId: service.voucherEntityId,
            voucherEntityServiceId: service.voucherEntityServiceId,
            used: false,
            paymentMethodSource: pm,
          })
        }
      })
    })
    let reduction = 0

    // Assign voucher info to services and calculate reduction
    selectedServices.value.forEach(service => {
      // Find first available pass service for this serviceId
      const passService = availablePassServices.find(p =>
        p.serviceId === Number(service.id) && !p.used,
      )
      if (passService) {
        reduction += Number(service.finalPrice ?? 0)
        passService.used = true
        // Assign voucher info to the service
        service.voucherEntityId = passService.voucherEntityId
        service.voucherEntityServiceId = passService.voucherEntityServiceId
        //}
        passService.paymentMethodSource.used = true
      } else {
        // Reset voucher info if no pass available
        service.voucherEntityId = null
        service.voucherEntityServiceId = null
      }
    })

    return reduction
  })

  // 👉 Sum of prepayment methods
  const prepaymentMethodTotal = computed(() => Number(selectedPrepaymentMethod.value.amount) ?? 0)
  // 👉 Sum of payment methods
  const paymentMethodsTotal = computed(() => {
    // Ensure selectedPaymentMethods and paymentMethods are defined
    if (!selectedPaymentMethods.value) {
      return 0
    }

    return Number(selectedPaymentMethods.value.reduce((sum, method) => Number(sum) + Number(method.amount || 0), 0))
  })
  // 👉 Sum of prepayment and payment methods
  const preAndPaymentMethodsTotal = computed(() => {
    return Number(prepaymentMethodTotal.value) + Number(paymentMethodsTotal.value)
  })


  //  👉 Reset event
  const resetSelectedEvent = async (clearSelectedCustomer = true) => {
    if (!allowResetEvent.value) return
    //console.log('%cresetSelectedEvent', 'color:orange;')
    selectedEvent.value.id = null // M: changed from ""
    selectedEvent.value.eventId = 0 // 0 - normal, 1 - note, 2 - blockage
    selectedEvent.value.status = 2
    selectedEvent.value.statusName = ""
    selectedEvent.value.type = 0 // 0 - normal, 1 - note, 2 - blockage
    selectedEvent.value.title = ""
    selectedEvent.value.dateStart = ""
    selectedEvent.value.timeStart = ""
    selectedEvent.value.timeEnd = ""
    selectedEvent.value.client = ""
    selectedEvent.value.employeeId = null
    selectedEvent.value.salonerClientId = ""
    selectedEvent.value.description = ""
    selectedEvent.value.services = []
    selectedEvent.value.reserveListEkey = ""
    selectedEvent.value.servicesMode = "sequential"
    selectedEvent.value.wantedNotif = false // m: zmieniłem po konsultacji z Magdą i Łukaszem
    selectedEvent.value.cyclicalForFuture = []
    selectedEvent.value.canceled = null
    selectedEvent.value.selectedPaymentMethods = []
    selectedPaymentMethods.value = []
    selectedEvent.value.files = [] // Files cleaning
    newFiles.value = [] // Files cleaning
    //existingFiles.value = [] // Files cleaning
    areServicesScattered.value = false
    if(clearSelectedCustomer) customerStore.clearSelectedCustomer()
    selectedServices.value = []
    selectedProducts.value = []
    selectedEmployee.value = null
    tipAmount.value = null
    isCyclical.value = false
    cyclicVisits.value = []
    await nextTick()
    allowSave.value = true
    isModified.value = false
    isWatcherActive.value = false
    selectedEvent.value.clientInSalon = false
    selectedEvent.value.clientConfirmed = false
    selectedEvent.value.clientComment = ''
    selectedEvent.value.tags = []
    selectedProductType.value = 1 // set to sale (default)
    reserveListItemId.value = null
    selectedPrepaymentMethod.value = { ...emptyPrepaymentMethod }
    isPrepaymentSectionDisabled.value = false
    // console.warn('RESET SelectedEvent FIRED!!!', selectedEvent.value.id)

    visitView.value = true
    blockadeOrNoteView.value = false
    cyclicVisitView.value = false

    actions.value.showEdit = false
    actions.value.showSave = true
    actions.value.showCancel = false
    actions.value.showDelete = false
    actions.value.showRestore = false
  }

  // Watcher for changes in event
  const isWatcherActive = ref(false)
  watch(
    [selectedEvent, selectedProducts, selectedServices, selectedPrepaymentMethod, selectedPaymentMethods],
    () => {
      if (isWatcherActive.value) {
        isModified.value = true
      }
    },
    { deep: true },
  )
  watch(
    [
      () => selectedDate.value,
      () => selectedServices.value,
    ],
    () => {
      if (isWatcherActive.value) {
        updateSelectedEventOnFullcalendar()
        updateSelectedPrepaymentAmount()
      }
    },
    { deep: true },
  )

  // 👉 Cycle visits handlers
  const cyclicRepetitionNumber = ref(1)
  const cyclicRepetitionTimes = ref(5)
  const cyclicVisits = ref([]) // Array of cyclic visits
  const areCycleVisitsLoading = ref(false)

  // Calculation of cyclic visits
  const calculateCycleVisits = async (visitsData, single = false) => {
    areCycleVisitsLoading.value = true

    try {
      const response = await $backend(`/events/recurringSlots`, {
        method: 'POST',
        body: visitsData,
      })

      if (response.success && single === false) {
        message.value = response.message
        cyclicVisits.value = response.data.map((item, index) => ({
          id: generateUniqueId() + '_' + index,
          available: item.available,
          startDate: item.startDate,
          startTime: item.startTime,
          endTime: item.endTime,
          employees: item.employees || [],
          services: visitsData.services || [],
          errors: item.errors || [],
          originalStartTime: item.startTime,
          originalDuration: timeToMinutes(item.endTime) - timeToMinutes(item.startTime),
        }))
      } else if (response.success && single === true) {
        const item = {
          ...response.data[0],
          id: generateUniqueId() + '_' + cyclicVisits.value.length,
          available: response.data[0].available,
          startDate: response.data[0].startDate,
          startTime: response.data[0].startTime,
          endTime: response.data[0].endTime,
          employees: response.data[0].employees || [],
          services: visitsData.services || [],
          errors: response.data[0].errors || [],
          originalStartTime: response.data[0].startTime,
          originalDuration: timeToMinutes(response.data[0].endTime) - timeToMinutes(response.data[0].startTime),
        }
        cyclicVisits.value.push(item)
      }
      areCycleVisitsLoading.value = false

      return response
    } catch (error) {
      error.value = error
      console.error('Error handling cycle visits calc:', error)
      areCycleVisitsLoading.value = false

      return []
    }

  }
  // Find available visit slot if slot is not available // m: propably will be removed
  const findAvailableVisitSlot = async (data) => {
    areCycleVisitsLoading.value = true

    try {
      // Build query params
      const queryParams = new URLSearchParams()
      queryParams.append('startDate', data.startDate)
      queryParams.append('startTime', data.startTime)
      queryParams.append('endTime', data.endTime)
      queryParams.append('employee', data.employee)

      // Handle resources array
      if (data.resources && Array.isArray(data.resources)) {
        data.resources.forEach(resource => {
          queryParams.append('resources[]', resource)
        })
      }

      const response = await $backend(`/events/alternativeSlot?${queryParams.toString()}`, {
        method: 'GET',
      })

      if (response.success) {
        message.value = response.message
      }
      areCycleVisitsLoading.value = false

      return response
    } catch (error) {
      error.value = error
      console.error('Error handling free visit slot:', error)
      areCycleVisitsLoading.value = false

      return {}
    }
  }

  //Find first available slot earlier or later
  const findFreeSlot = async (payload) => {
    areCycleVisitsLoading.value = true
    try {
      const response = await $backend(`/events/findFreeSlot`, {
        method: 'POST',
        body: payload,
      })

      if (response.success) {
        message.value = response.message
      }
      areCycleVisitsLoading.value = false

      return response
    } catch (error) {
      error.value = error
      console.error('Error handling free visit slot:', error)
      areCycleVisitsLoading.value = false

      return {}
    }
  }

  // Check availability of cyclic visit with given new data
  const checkCyclicVisitAvailability = async (data) => {
    areCycleVisitsLoading.value = true
    try {
      const response = await $backend(`/events/checkSlotsAvailability`, {
        method: 'POST',
        body: data,
      })

      if (response.success) {
        message.value = response.message
      }
      areCycleVisitsLoading.value = false

      return response
    } catch (error) {
      console.error('Error checking availability for new visit slot:', error)
      areCycleVisitsLoading.value = false

      return {}
    }

  }

  const updateCyclicVisit = (itemId, payload) => {
    try {
      // Find the visit index
      const visitIndex = cyclicVisits.value.findIndex(v => v.id === itemId)
      if (visitIndex === -1) return false

      // Handle array payload (take the first item if it's an array)
      const visitData = Array.isArray(payload) ? payload[0] : payload

      if (!visitData) {
        console.error('Invalid payload structure for updateCyclicVisit')

        return false
      }

      const updatedItem = {
        ...cyclicVisits.value[visitIndex],
        available: visitData.available,
        startDate: visitData.startDate,
        startTime: visitData.startTime,
        endTime: visitData.endTime,
        employees: visitData.employees,
        errors: visitData.errors || [],
      }

      // Replace the item in the array to maintain reactivity
      cyclicVisits.value = cyclicVisits.value.map(item =>
        item.id === itemId ? updatedItem : item)

      updateSelectedEventOnFullcalendar()

      return true
    } catch (error) {
      console.error('Error updating cyclic visit:', error)

      return false
    }
  }

  const generateTempEvent = (resourceId, start, end, services) => {
    return {
      editable: false,
      durationEditable: false,
      startEditable: false,
      resourceEditable: false,
      resourceId: resourceId,
      id: 'temp-event',
      title: '',
      start: start,
      end: end,
      allDay: false,
      backgroundColor: 'rgba(232,74,7,0.7)',
      borderColor: '#e84a07',
      classNames: ['selected-event'],
      services: services,
    }
  }

  // Hides original selected event on FC
  const hideOriginalEventsOnFullcalendar = () => {
    const eventsOnFc = calendarStore.calendarApi.getEvents().filter(e => String(e.id) === String(selectedEvent.value.id))
    eventsOnFc.forEach(event => {
      //event.remove()
      const startTime = event.start ? event.start.toTimeString().slice(0, 8) : '00:00:00'
      const endTime = event.end ? event.end.toTimeString().slice(0, 8) : '00:00:00'
      event.setProp('editable', true)
      event.setProp('startEditable', true)
      event.setStart(`1970-01-01T${startTime}`)
      event.setEnd(`1970-01-01T${endTime}`)
    })

  }

  // Restores previously selected event on FC
  const showOriginalEventsOnFullcalendar = () => {
    const eventsOnFc = calendarStore.calendarApi.getEvents().filter(e => String(e.id) === String(selectedEvent.value.id))
    eventsOnFc.forEach(event => {
      const startTime = event.start ? event.start.toTimeString().slice(0, 8) : '00:00:00'
      const endTime = event.end ? event.end.toTimeString().slice(0, 8) : '00:00:00'
      event.setStart(`${selectedEvent.value.dateStart}T${startTime}`)
      event.setEnd(`${selectedEvent.value.dateStart}T${endTime}`)
      if (event.extendedProps.active !== "1" || event.extendedProps.isResource) {
        event.setProp('editable', false)
        event.setProp('startEditable', false)
      }
    })
  }

  // Removes temporary selected events on calendar (the ones with ORANGE bg;-)
  const removeSelectedEventsFromFullcalendar = () => {
    const oldSelectedEvents = calendarStore.calendarApi.getEvents().filter(event =>
      event.classNames.includes('selected-event'),
    )
    oldSelectedEvents.forEach(event => event.remove())
  }

  const updateSelectedEventOnFullcalendar = debounce(() => {
    //console.warn('updateSelectedEventOnFullcalendar')
    if (!calendarStore.calendarApi) return
    if (modalStore.modals.calendarSales) return // Dont update event on FC when we doing sales only

    // Remove all events with selected-event class
    removeSelectedEventsFromFullcalendar()

    let dateStart = extractDate(selectedDate.value, '-') || selectedEvent.value.dateStart
    let minutesToAdjust = 0

    // cyclic visit handling
    if (isCyclical.value && cyclicVisitView.value && modalStore.modals.cycleVisitDialog && cyclicVisits.value?.length) {
      const currentCalendarDate = extractDate(calendarStore.calendarApi.getDate(), "-")
      const currentCyclicVisit = cyclicVisits.value.find(cyclicVisit => cyclicVisit.startDate === currentCalendarDate)
      if (currentCyclicVisit) minutesToAdjust = getMinutesDifference(selectedStartTime.value, currentCyclicVisit.startTime)
    }

    // console.log('selectedEvent', selectedEvent.value)

    // If no services or all services have the same employeeId
    if (selectedServices.value.length === 0 || selectedServices.value.every(service => service.employeeEkey === selectedServices.value[0].employeeEkey)) {
      let tmpTimeStart = selectedEvent.value.timeStart
      let tmpTimeEnd = selectedEvent.value.timeEnd

      if(!tmpTimeStart && !tmpTimeEnd) return // B: it happens when we have opened event -> client card -> history -> open another event

      let start = dateStart + " " + tmpTimeStart
      let end = dateStart + " " + tmpTimeEnd
      let employeeEkey = selectedEvent.value.employeeEkey
      if (selectedServices.value.length > 0) {
        employeeEkey = selectedServices.value[0].employeeEkey
      }
      start = adjustDateTimeByMinutes(start, minutesToAdjust)
      end = adjustDateTimeByMinutes(end, minutesToAdjust)
      const tempEvent = generateTempEvent(employeeEkey, start, end, blockadeOrNoteView.value ? [] : selectedServices.value)
      tempEvent.id = `temp-event-0`
      calendarStore.calendarApi.addEvent(tempEvent)
    } else {
      // Group consecutive services by the same employee
      let currentGroup = []
      let eventIndex = 0

      selectedServices.value.forEach((service, index) => {

        const isLastService = index === selectedServices.value.length - 1
        const nextService = selectedServices.value[index + 1]

        currentGroup.push(service)

        // If next service has different employee or this is the last service, create event for current group
        if (isLastService || (nextService && service.employeeEkey !== nextService.employeeEkey)) {
          const firstService = currentGroup[0]
          const lastService = currentGroup[currentGroup.length - 1]

          let start = firstService.startTime ? (dateStart + " " + firstService.startTime) : firstService.start
          let end = lastService.endTime ? (dateStart + " " + lastService.endTime) : firstService.end
          start = adjustDateTimeByMinutes(start, minutesToAdjust)
          end = adjustDateTimeByMinutes(end, minutesToAdjust)

          const tempEvent = generateTempEvent(firstService.employeeEkey, start, end, currentGroup)
          tempEvent.id = `temp-event-${eventIndex}`
          calendarStore.calendarApi.addEvent(tempEvent)

          // Reset group and increment index
          currentGroup = []
          eventIndex++
        }
      })
    }

    // Handle resources for services -> generate TempEvent for each resource
    // B: być może w przyszłości zrobić jeszcez grupowanie nastepujacych po sobie uslug z tym samym pracownikiem - na razie to zostawiam
    let resourceEventIndex = 0
    selectedServices.value.forEach((service) => {
      // Check for resources
      if (service.resources && Array.isArray(service.resources) && service.resources.length > 0) {
        service.resources.forEach((resource) => {

          let start = service.startTime ? (dateStart + " " + service.startTime) : service.start
          let end = service.endTime ? (dateStart + " " + service.endTime) : service.end
          start = adjustDateTimeByMinutes(start, minutesToAdjust)
          end = adjustDateTimeByMinutes(end, minutesToAdjust)

          const tempResourceEvent = generateTempEvent(resource.id, start, end, [service])

          tempResourceEvent.id = `temp-resource-event-${resourceEventIndex}`
          calendarStore.calendarApi.addEvent(tempResourceEvent)

          resourceEventIndex++
        })
      }
    })

    // B: tmp off
    // document.querySelectorAll('.selected-event').forEach(el => {
    //   const parent = el.parentElement
    //   if (parent) {
    //     parent.style.zIndex = '999'
    //   }
    // })
  }, 300)

  // Remove visit
  const removeEvent = async (eventId, includeCyclicEvents = false) => {
    isSaving.value = true
    try {
      return await $backend(`/events/delete`, {
        method: 'POST',
        body: {
          eventId: eventId,
          includeCyclicEvents, // always NO
        },
      })
    } catch (error) {
    }
    isSaving.value = false

    return {}
  }

  //---------- 👉 Loyalty points calculation: ----------//
  // service.loyaltyPoints === null → multiply finalPrice by loyaltyPointsForService
  // service.loyaltyPoints === 0 → 0 points (use the explicit 0 value)
  // service.loyaltyPoints > 0 → use that specific value directly
  // 👉 Helper computed for calculate earned loyalty points for service including exception
  const getServicesLoyaltyPoints = () => {
    const pointsPerService = calendarStore.loyaltyProgramSettings.loyaltyPointsForService || 0

    return selectedServices.value.reduce((total, service) => {
      const servicePrice = Number(service.finalPrice ?? serviceStore.calculateServicePrice(
        service.price,
        service.discountType,
        service.discountValue,
      ))

      const serviceLoyaltyPoints = service.loyaltyPoints

      // If loyaltyPoints is 0, use that explicit 0 value
      if (serviceLoyaltyPoints === 0) {
        return total
      }

      // If loyaltyPoints > 0, use that specific value (ignore default calculation)
      if (serviceLoyaltyPoints !== null && serviceLoyaltyPoints > 0) {
        return total + Number(serviceLoyaltyPoints)
      }

      // For null, use default calculation (multiply by pointsPerService)
      return total + Math.floor(servicePrice * pointsPerService)
    }, 0)
  }

  // Calculate loyalty points earned for the visit
  const calculateEarnedLoyaltyPoints = (productsTotal, vouchersTotal) => {

    //points gained per 1zł per service, product and voucher
    // let pointsPerService = calendarStore.loyaltyProgramSettings.loyaltyPointsForService || 0
    let pointsPerProduct = calendarStore.loyaltyProgramSettings.loyaltyPointsForProduct || 0
    let pointsPerVoucher = calendarStore.loyaltyProgramSettings.loyaltyPointsForVoucher || 0

    //console.log('pointsPerService:', pointsPerService, 'pointsPerProduct:', pointsPerProduct, 'pointsPerVoucher:', pointsPerVoucher)
    //console.log('servicesTotal:', servicesTotal, 'productsTotal:', productsTotal, 'vouchersTotal:', vouchersTotal)

    // Calculate points for each category
    // let servicePoints = Math.floor(servicesTotal * pointsPerService)
    const servicePoints = getServicesLoyaltyPoints()
    let productPoints = Math.floor(productsTotal * pointsPerProduct)
    let voucherPoints = Math.floor(vouchersTotal * pointsPerVoucher)

    return loyaltyPointsEarned.value = servicePoints + productPoints + voucherPoints
  }

  // Check if same client has other visits same day
  const checkForCustomersOtherVisits = () => {
    // console.warn('eventStore.selectedEvent.id', eventStore.selectedEvent.id)
    // console.warn('eventStore.selectedCustomer?.salonerClientId', eventStore.selectedCustomer?.salonerClientId)
    // console.warn('asd', calendarStore.fetchedEvents.filter(e => e.editable && !e.isResource && e.salonerClientId === eventStore.selectedCustomer?.salonerClientId && eventStore.selectedEvent.id !== e.id).length)
    // console.warn('editable', calendarStore.fetchedEvents.filter(e => e.editable))
    // console.warn('!isResource', calendarStore.fetchedEvents.filter(e => e.editable && !e.isResource))
    // console.warn('salonerClientId', calendarStore.fetchedEvents.filter(e => e.editable && !e.isResource && String(e.salonerClientId) === String(eventStore.selectedCustomer?.salonerClientId)))
    // console.warn('selectedEvent.id', calendarStore.fetchedEvents.filter(e => e.editable && !e.isResource && String(e.salonerClientId) === String(eventStore.selectedCustomer?.salonerClientId) && String(eventStore.selectedEvent.id) !== String(e.id)))
    //console.warn('salonerClientId', calendarStore.fetchedEvents.filter(e => e.editable && !e.isResource && e.salonerClientId === eventStore.selectedCustomer?.salonerClientId && eventStore.selectedEvent.id !== e.id).length)
    return (calendarStore.fetchedEvents.filter(e => e.editable && !e.isResource && String(e.salonerClientId) === String(selectedCustomer.value?.salonerClientId) && String(selectedEvent.value?.id) !== String(e.id)).length >= 1)
  }

  return {
    // ---------- Core Event State ----------
    selectedEvent,          // Current event being edited or created
    isModified,             // Whether event has been modified
    allowSave,              // Whether editing is allowed
    isLoading,              // Loading state
    isSaving,               // Saving state
    message,                // Success message
    error,                  // Error message
    actions,
    allowResetEvent,        // Whether resetting event is allowed
    // ---------- Customer State ----------
    selectedCustomer,       // Currently selected customer
    // customerInSalon,        // Whether customer is present in salon
    // visitConfirmed,         // Whether visit is confirmed
    selectedLoyaltyCoupon,
    loyaltyPointsEarned,

    // ---------- Date & Time State ----------
    selectedDate,           // Selected date for event
    selectedStartTime,      // Start time for event
    selectedEndTime,        // End time for event
    allowRecalculation,     // Whether automatic end time recalculation is allowed

    // ---------- Services & Employee State ----------
    selectedServices,       // Array of selected services
    selectedEmployee,       // Selected employee performing service
    // servicesAndProductsFinalPrice,     // Total price of all services
    servicesTotalDuration,  // Total duration of all services
    totalDiscountAmount,    // Total amount of discounts
    currentService,         // Currently edited service
    selectedProducts,       // Array of products sold with this visit
    selectedSeller,         // Currently selected seller (employee)
    selectedProductType,    // Currently selected sale type

    // ---------- Payment State ----------
    prepaymentMethodTotal,                // Total prepayment amount
    finalPrice,                      // Event value with (included discounts from coupon etc.)
    finalPriceWithNoDiscount,        // Event value with no discounts
    getServicesTotal,                // Services total value
    getProductsTotal,                // Products total value
    tipAmount,                       // Tip amount
    selectedPaymentMethods,          // Selected payment options
    balanceAmount,                   // Change amount
    preAndPaymentMethodsTotal,       // Total of all prepayment and payment options
    passesTotalReduction,            // How much reduction is being made from services in passes
    paymentMethodsTotal,             // Total of all payment methods
    emptyPrepaymentMethod,           // Empty prepayment method template
    isPrepaymentSectionDisabled,     // Whether prepayment section is disabled
    selectedPrepaymentMethod,        // Currently selected prepayment method

    // ---------- UI View State ----------
    blockadeOrNoteView,     //
    visitView,              //
    cyclicVisitView,        // Whether in cyclical visit view
    isWatcherActive,        // Whether watcher for changes is active
    wasFinalisedAlready,    // Whether event was already finalized
    reserveListItemId,      // ID for reserve list
    showFinalization,       // Shows finalisation section
    validationErrors,       // Object containing validation errors for different fields
    newFiles,               // Files selected for future upload
    //existingFiles,          // Existing files after user modifications
    clearValidationErrors,  // Reset all validation errors


    // ---------- Cyclical Visits State ----------
    isCyclical,                 // Whether event is cyclical
    cyclicRepetitionTimes,      // How many times to repeat
    cyclicRepetitionNumber,     // Repetition number
    cyclicVisits,               // Array of cyclical visits
    areCycleVisitsLoading,      // Whether cycle visits are loading

    // ---------- Calendar Methods ----------
    setCalendarApi,             // Set calendar API reference
    getCalendarApi,             // Get calendar API reference

    // ---------- Core Event Methods ----------
    resetSelectedEvent,         // Reset event to default values
    updateSelectedEventOnFullcalendar,
    removeSelectedEventsFromFullcalendar,
    hideOriginalEventsOnFullcalendar,
    showOriginalEventsOnFullcalendar,
    removeEvent,

    // ---------- Service Methods ----------
    getServiceResources,        // Get resources for a service
    getResourceById,            // Get resource by ID
    addServiceToEvent,          // Add service to event
    detectConflictsInServicesAndEmployeeWorkingHours, // Check employees working hours vs selected services
    addProductToEvent,          // Add product to event
    deleteServiceFromEvent,     // Delete service from event
    updateServiceChanges,       // Update service changes
    setCurrentService,          // Set currently edited service
    clearCurrentService,        // Clear currently edited service
    calculateEarnedLoyaltyPoints, // Calculate loyalty points earned for the visit
    updateServiceResourcesStatus, // Updates service resources statues (collision detection)
    updateSelectedPrepaymentAmount,
    adjustServicesTimes,            // Adjust service times after eg. time pickers change
    areServicesScattered,           // True if service n endTime is !== n+1 startTime

    // ---------- Time Calculation Methods ----------
    recalculateStartTime,           // Recalculate start time based on services
    recalculateEndTime,             // Recalculate end time based on services
    setAllowEndTimeRecalculation,   // Toggle end time recalculation
    recalculateAllServicesTime,     // Recalculate all service times
    hasAdjacencyMisalignment,       // Check if services were changed on FC and are not aligned one after another


    // ---------- Cyclical Visit Methods ----------
    calculateCycleVisits,           // Calculate cyclical visits
    findAvailableVisitSlot,         // Find available visit slot
    updateCyclicVisit,              // Update cyclical visit
    checkCyclicVisitAvailability,   // Check availability of cyclical visit
    findFreeSlot,

    // ---------- Customer methods ------------
    checkForCustomersOtherVisits,

  }
})
